import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { ConfigProvider } from "antd";
import antdTheme from "./theme/antdTheme";

import Dashboard from "./pages/Dashboard";
import GroupSummary from "./pages/GroupSummary";
import GroupDetail from "./pages/GroupDetail";
import Profile from "./pages/Profile";
import Login from "./js/components/login/Login.react";
import "./js/components/login/login.css";

// Group Management
import GroupManagement from "./pages/GroupManagement";
import Onboard from "./pages/Onboard";
import Offboard from "./pages/Offboard";

// Systems
import SystemStatus from "./pages/SystemStatus";
import SystemSearch from "./pages/SystemSearch";
import SystemDetail from "./pages/SystemDetail";

// Control Group
import ControlGroupSummary from "./pages/ControlGroupSummary";
import ControlGroupDetail from "./pages/ControlGroupDetail";
import BiddingSummary from "./pages/BiddingSummary";
import BiddingRules from "./pages/BiddingRules";

// Account Management
import AccountSummary from "./pages/AccountSummary";
import UserManagement from "./pages/UserManagement";
import RoleManagement from "./pages/RoleManagement";
import Statement from "./pages/Statement";

// Tickets
import TicketsSummary from "./pages/TicketsSummary";
import TicketsSearch from "./pages/TicketsSearch";
import TicketDetail from "./pages/TicketDetail";

// Dispatch
import DispatchSummary from "./pages/DispatchSummary";
import CurrentDispatch from "./pages/CurrentDispatch";
import CurrentDispatchDetail from "./pages/CurrentDispatchDetail";
import BookedDispatch from "./pages/BookedDispatch";
import DispatchReport from "./pages/DispatchReport";
import StrategyCentre from "./pages/StrategyCentre";

// Company
import CompanyList from "./pages/CompanyList";
import CompanySnapshot from "./pages/CompanySnapshot";
import CompanyAPI from "./pages/CompanyAPI";

// Settlements
import SettlementRuns from "./pages/SettlementRuns";
import SettlementRunDetail from "./pages/SettlementRunDetail";
import SettlementRuleManagement from "./pages/SettlementRuleManagement";
import OutstandingReceivable from "./pages/OutstandingReceivable";
import OutstandingPayable from "./pages/OutstandingPayable";
import HistoricalReceivable from "./pages/HistoricalReceivable";
import HistoricalPayments from "./pages/HistoricalPayments";
import SpecialJournals from "./pages/SpecialJournals";

// Configuration
import Equipment from "./pages/Equipment";
import VppPlans from "./pages/VppPlans";

// Audit Log
import AuditLog from "./pages/AuditLog";

// Bug Reports
import BugReportList from "./pages/BugReportList";
import BugReportDetail from "./pages/BugReportDetail";
import BugReportForm from "./pages/BugReportForm";

// Core Services
import EngineStatus from "./pages/EngineStatus";
import AemoSettlementConfig from "./pages/AemoSettlementConfig";
import RedisStatus from "./pages/RedisStatus";
import Backtest from "./pages/Backtest";

function isAuthenticated() {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    return Boolean(token || userId);
  } catch (_) {
    return false;
  }
}

function getDefaultRoute() {
  if (!isAuthenticated()) return "/login";
  const roleType = localStorage.getItem("roleType") || "";
  if (roleType === "SUPERADMIN") return "/dashboard";
  try {
    const perms = JSON.parse(localStorage.getItem("permissions") || "[]");
    if (perms.includes("dashboard:read")) return "/dashboard";
  } catch (_) {}
  return "/profile";
}

function ProtectedRoute() {
  return isAuthenticated() ? <Outlet /> : <Navigate to="/login" replace />;
}

// Check if user has at least one of the required permissions (superadmin bypasses)
function hasPermission(requiredPerms) {
  const roleType = localStorage.getItem("roleType") || "";
  if (roleType === "SUPERADMIN") return true;
  if (!requiredPerms || requiredPerms.length === 0) return true;
  try {
    const perms = JSON.parse(localStorage.getItem("permissions") || "[]");
    return requiredPerms.some((p) => perms.includes(p));
  } catch (_) {
    return false;
  }
}

// Route guard: requires authentication + specific permissions
function PermissionRoute({ permissions, children }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!hasPermission(permissions)) return <Navigate to="/profile" replace />;
  return children;
}

function DefaultRedirect() {
  return <Navigate to={getDefaultRoute()} replace />;
}

export default function App() {
  return (
    <ConfigProvider theme={antdTheme}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<DefaultRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<PermissionRoute permissions={["dashboard:read"]}><Dashboard /></PermissionRoute>} />
          <Route path="/dashboard/:userId" element={<PermissionRoute permissions={["dashboard:read"]}><Dashboard /></PermissionRoute>} />

          <Route element={<ProtectedRoute />}>
            <Route path="/profile" element={<Profile />} />
          </Route>

          {/* Group Management */}
          <Route path="/group-management" element={<GroupManagement />} />
          <Route path="/group-management/groups" element={<GroupSummary />} />
          <Route path="/group-management/groups/:groupId" element={<GroupDetail />} />
          <Route path="/group-management/onboard" element={<Onboard />} />
          <Route path="/group-management/offboard" element={<Offboard />} />

          {/* Systems */}
          <Route path="/systems/summary" element={<SystemStatus />} />
          <Route path="/systems/search" element={<SystemSearch />} />
          <Route path="/systems/:systemSn" element={<SystemDetail />} />

          {/* Control Group */}
          <Route path="/control-group/summary" element={<ControlGroupSummary />} />
          <Route path="/control-group/:groupId" element={<ControlGroupDetail />} />

          {/* Bidding */}
          <Route path="/bidding/summary" element={<BiddingSummary />} />
          <Route path="/bidding/rules" element={<BiddingRules />} />

          {/* Account Management */}
          <Route path="/account/summary" element={<AccountSummary />} />
          <Route path="/account/users" element={<UserManagement />} />
          <Route path="/account/statement" element={<Statement />} />

          {/* Roles & Permissions (Superadmin only) */}
          <Route path="/roles" element={<RoleManagement />} />
          <Route path="/configuration/equipment" element={<Equipment />} />
          <Route path="/configuration/vpp-plans" element={<VppPlans />} />

          {/* Tickets */}
          <Route path="/tickets/summary" element={<TicketsSummary />} />
          <Route path="/tickets/search" element={<TicketsSearch />} />
          <Route path="/tickets/:ticketId" element={<TicketDetail />} />

          {/* Dispatch */}
          <Route path="/dispatch/summary" element={<DispatchSummary />} />
          <Route path="/dispatch/current" element={<CurrentDispatch />} />
          <Route path="/dispatch/current/:dispatchId" element={<CurrentDispatchDetail />} />
          <Route path="/dispatch/booked" element={<BookedDispatch />} />
          <Route path="/dispatch/report" element={<DispatchReport />} />
          <Route path="/dispatch/strategy" element={<StrategyCentre />} />

          {/* Company */}
          <Route path="/company/list" element={<CompanyList />} />
          <Route path="/company/snapshot" element={<CompanySnapshot />} />
          <Route path="/company/snapshot/:companyId" element={<CompanySnapshot />} />

          {/* Settlements */}
          <Route path="/settlements/runs" element={<PermissionRoute permissions={["settlement:read"]}><SettlementRuns /></PermissionRoute>} />
          <Route path="/settlements/runs/:runId" element={<PermissionRoute permissions={["settlement:read"]}><SettlementRunDetail /></PermissionRoute>} />
          <Route path="/settlements/rules" element={<PermissionRoute permissions={["settlement:run"]}><SettlementRuleManagement /></PermissionRoute>} />
          <Route path="/settlements/journals" element={<PermissionRoute permissions={["settlement:read"]}><SpecialJournals /></PermissionRoute>} />
          <Route path="/settlements/receivable" element={<PermissionRoute permissions={["settlement:read"]}><OutstandingReceivable /></PermissionRoute>} />
          <Route path="/settlements/payable" element={<PermissionRoute permissions={["settlement:read"]}><OutstandingPayable /></PermissionRoute>} />
          <Route path="/settlements/history-receivable" element={<PermissionRoute permissions={["settlement:read"]}><HistoricalReceivable /></PermissionRoute>} />
          <Route path="/settlements/history-payments" element={<PermissionRoute permissions={["settlement:read"]}><HistoricalPayments /></PermissionRoute>} />

          {/* API */}
          <Route path="/api" element={<CompanyAPI />} />

          {/* Audit Log */}
          <Route path="/audit-log" element={<AuditLog />} />

          {/* Bug Reports */}
          <Route path="/bug-reports" element={<BugReportList />} />
          <Route path="/bug-reports/new" element={<BugReportForm />} />
          <Route path="/bug-reports/:id" element={<BugReportDetail />} />

          {/* Core Services */}
          <Route path="/core-services/engines" element={<EngineStatus />} />
          <Route path="/core-services/aemo-settlement" element={<AemoSettlementConfig />} />
          <Route path="/core-services/redis" element={<RedisStatus />} />
          <Route path="/core-services/backtest" element={<Backtest />} />
          <Route path="/core-services/backtest/:jobId" element={<Backtest />} />

          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}
